// core
import React from 'react';
import { Link } from 'react-router-dom'

// settings + utils
import Settings from 'config.js';
import Auth from 'services/Auth.js';
import API from 'utils/API.js';
import CSV from 'utils/CSV.js';
import XHR from 'components/lib/swell-ui-0.0.3/utils/XHR';
import SchoolsUtil from 'utils/SchoolsUtil';

// lib
import Page from 'pages/shared/Page';
import Spinner from 'components/shared/Spinner';

// shared
import PageHeader from 'components/shared/PageHeader';
import ButtonsToolbar from 'components/shared/ButtonsToolbar';
import FiltersToolbar from 'components/shared/filters/FiltersToolbar';
import SortingToolbar from 'components/shared/SortingToolbar';

import MainHeading from 'components/shared/MainHeading';
import PageDataWrapper from 'pages/shared/PageDataWrapper';
import NoResultsMessage from 'components/shared/NoResultsMessage';

// page-specific/etc.
import TeacherList from 'components/teachers/TeacherList';

class TeachersOverviewPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      // XHR stuff
      queue: {},
      fetching: false,
      error: '',

      // filters
      filters: {
        schooltypes: [],

        schoolyears: [],

        locations: [],

        projects: [],
        productGroups: [],
        products: []
      },
      showFiltersRestoreNotice: false,
      searchKey: '',

      // this is the object(s) before any filtering/search
      fetchedData: {
        teachers: []
      },

      // current object(s)
      currData: {
        teachers: [],

        sortBy: 'teacher_name',
        sortDir: 'ASC',

        offset: 0,
        limit: 500
      }

      // etc.
    };

    this.abortXHRQueue = this.abortXHRQueue.bind(this);

    this.handleFilters = this.handleFilters.bind(this);
    this.handleSearchUpdate = this.handleSearchUpdate.bind(this);
    this.fetchTeachers = this.fetchTeachers.bind(this);
    this.refreshTeachers = this.refreshTeachers.bind(this);

    this.handleSortingChange = this.handleSortingChange.bind(this);
    this.sort = this.sort.bind(this);

    this.handlePaginationChange = this.handlePaginationChange.bind(this);

    this.downloadCSV = this.downloadCSV.bind(this);

    this.handleRecipientListUpdate = this.handleRecipientListUpdate.bind(this);
    this.handleSelectAllRecipients = this.handleSelectAllRecipients.bind(this);
  }

  componentWillMount() {
    const currFiltersJSON = window.localStorage.getItem('colibri:teachers-overview:filters');
    if (currFiltersJSON) {
      const currFilters = JSON.parse(currFiltersJSON);

      this.setState({
        filters: currFilters,
        showFiltersRestoreNotice: true
      }, () => {
        window.setTimeout(() => {
          this.setState({
            showFiltersRestoreNotice: false
          });
        }, 2000);

        this.fetchTeachers(this.state.filters, '');
      });
    } else {
      this.fetchTeachers(this.state.filters, '');
    }
  }

  componentWillUnmount() {
    this.abortXHRQueue();
  }

  abortXHRQueue() {
    // abort all Ajax calls
    Object.keys(this.state.queue).forEach((key) => {
      // console.log('got controller');

      const controller = this.state.queue[key];
      if (controller) {
        XHR.abort(controller);
      }
    });
  }

  handleFilters(data) {
    this.setState({
      filters: data
    }, () => {
      // save for later
      const currFiltersJSON = JSON.stringify(data);
      window.localStorage.setItem('colibri:teachers-overview:filters', currFiltersJSON);

      this.refreshTeachers();
    });
  }

  fetchTeachers(params, searchKey) {
    this.setState({
      error: ''
    }, () => {
      const endpoint = Settings.V3_API_URL + '/teachers?&offset=0&limit=-1';

      this.abortXHRQueue();// we are retrieving teachers only with ajax, so all other calls should be aborted

      const queueKey = 'fetch_teachers_filters';
      const controller = XHR.fetch('GET', endpoint + '&t=' + (new Date()).getTime(), {}, {
        headers: {
          'Authorization': 'Bearer ' + Auth.getToken()
        }
      }, (response, error) => {
        if (error) {
          this.setState((prevState) => {
            delete prevState.queue[queueKey];

            prevState.currData.teachers = [];
            prevState.error = error.message + ' while getting teachers';

            return prevState;
          });

          return false;
        }

        if (!response.data.teachers) {
          alert('Cannot get teachers! (no teachers in response)');

          console.error('cannot get teachers');
          // console.dir(response);

          return false;
        }

        this.setState((prevState) => {
          delete prevState.queue[queueKey];

          prevState.fetchedData.teachers = response.data.teachers;

          return prevState;
        }, () => {
          // console.log('TEACHERS OK!', response.data.teachers, this.state);
          this.refreshTeachers();
        });
      });

      this.setState((prevState) => {
        prevState.queue[queueKey] = controller;

        prevState.currEndpoint = endpoint;

        return prevState;
      });
    });
  }

  handleSearchUpdate(searchKey, e) {
    // console.log('handleSearchUpdate');

    this.setState((prevState) => {
      prevState.searchKey = searchKey;

      return prevState;
    }, () => {
      this.refreshTeachers();
    });
  }

  handleSortingChange(sortBy, sortDir) {
    this.setState((prevState) => {
      prevState.currData.sortBy = sortBy;
      prevState.currData.sortDir = sortDir;

      return prevState;
    }, () => {
      this.refreshTeachers();
    });
  }

  sort(items) {
    if (!this.state.currData.sortBy || !this.state.currData.sortDir) {
      alert('do NOT know how to sort :-/');

      return items;
    }

    items.sort((itemA, itemB) => {
      let a = '';
      let b = '';
      let prop = '';

      switch (this.state.currData.sortBy) {
        case 'teacher_name':
          a = itemA.first_name + ' ' + itemA.last_name;
          b = itemB.first_name + ' ' + itemB.last_name;
          break;
        case 'teacher_email':
          a = itemA.email;
          b = itemB.email;
          break;
        case 'school_name':
        case 'school_city_name':
        case 'school_province_name':
        case 'school_region_name':
          // NOTE: a teacher could have more than one school in theory, but we're taking the first one into consideration for simplicity, and because teachers with more than 1 schools are rare
          prop = this.state.currData.sortBy.replace('school_', '');
          a = itemA.schools_data[0][prop];
          b = itemB.schools_data[0][prop];
          break;
        case 'orders_num_orders':
          a = itemA.order_ids ? itemA.order_ids.length : 0;
          b = itemB.order_ids ? itemB.order_ids.length : 0;
        case 'orders_num_kits':
          a = itemA.kit_ids ? itemA.kit_ids.length : 0;
          b = itemB.kit_ids ? itemB.kit_ids.length : 0;
          break;
        case 'orders_num_students':
          a = itemA.teachers ? itemA.teachers.length : 0;
          b = itemB.teachers ? itemB.teachers.length : 0;
          break;
        case 'mailings_num_accepted':
        case 'mailings_num_delivered':
        case 'mailings_num_opened':
        case 'mailings_num_clicked':
        case 'mailings_num_complained':
        case 'mailings_num_failed':
          prop = this.state.currData.sortBy.replace('mailings_', '');
          a = itemA.mailings ? itemA.mailings[prop] : 0;
          b = itemB.mailings ? itemB.mailings[prop] : 0;
          break;
        // WTF!?
        default:
          alert('Wrong sorting prop, will die');

          return false;
      }

      // normalize
      if (window.parseFloat(a) == a && window.parseFloat(b) == b) {
        // value is float-like, cast to that
        a = window.parseFloat(a);
        b = window.parseFloat(b);
      } else if (window.parseInt(a) == a && window.parseInt(b) == b) {
        // value is int-like, cast to that
        a = window.parseInt(a);
        b = window.parseInt(b);
      } else {
        // it's a string value, clean it up
        a = a ? String(a) : '';
        b = b ? String(b) : '';
        a = a.trim();
        b = b.trim();
        a = a.toLowerCase();
        b = b.toLowerCase();
      }

      if (a < b){
        return -1;
      }
      if (a > b){
        return 1;
      }

      return 0;
    });

    if (this.state.currData.sortDir == 'DESC') {
      items = items.reverse();
    }

    return items;
  }

  refreshTeachers() {
    // console.log('---');
    // console.dir(this.state.filters);
    // console.log('---');

    // filter teachers
    let teachersFiltered = [];

    this.state.fetchedData.teachers.forEach((teacher, teacherIndex) => {
      // filter by school types
      let schooltypesOK = false;

      if (this.state.filters.schooltypes && this.state.filters.schooltypes.length) {
        for (let i=0; i<this.state.filters.schooltypes.length; i++) {
          for (let j=0; j<teacher.school_types.length; j++) {
            const schooltypeGroup = SchoolsUtil.getSchoolclassGroupsFromType(teacher.school_types[j]);

            if (schooltypeGroup == this.state.filters.schooltypes[i]) {
              schooltypesOK = true;

              break;
            }
          }
        }
      } else {
        schooltypesOK = true;
      }

      // filter by locations
      let locationsOK = false;
      if (this.state.filters.locations && this.state.filters.locations.length) {
        for (let i=0; i<this.state.filters.locations.length; i++) {
          const location = this.state.filters.locations[i];

          if (
            teacher.school_region_ids.indexOf(location) != -1 ||
            teacher.school_province_ids.indexOf(location) != -1 ||
            teacher.school_city_ids.indexOf(location) != -1
          ) {
            locationsOK = true;

            break;
          }
        }
      } else {
        locationsOK = true;
      }

      // filter by schoolyears
      let schoolyearsOK = false;
      if (this.state.filters.schoolyears && this.state.filters.schoolyears.length) {
        for (let i=0; i<this.state.filters.schoolyears.length; i++) {
          let schoolyear = this.state.filters.schoolyears[i];

          // console.log('ts', typeof teacher.kit_schoolyears, teacher);
          if (teacher.kit_schoolyears && teacher.kit_schoolyears.indexOf(schoolyear) != -1) {
              schoolyearsOK = true;

              break;
          }
        }
      } else {
        schoolyearsOK = true;
      }

      // filter by products
      let productsOK = false;
      const products = this.state.filters.products;
      if (products.length) {
        if (teacher.kit_ids && teacher.kit_ids.length) {
          for (let i=0;i<products.length; i++) {
            const product = products[i];

            // console.log(product + ' vs. ' + teacher.kit_ids.join('|') + ' -> ' + (teacher.kit_ids.indexOf(product)));

            if (teacher.kit_ids.indexOf(parseInt(product)) != -1) {
              productsOK = true;

              // console.log('product OK');

              break;
            }
          }
        }
      } else {
        productsOK = true;
      }

      // filter by search
      let searchOK = false;
      if (this.state.searchKey) {
        const searchKey = this.state.searchKey.toLowerCase();

        ['id', 'first_name', 'last_name', 'email'].forEach((prop) => {
          if (teacher[prop] && teacher[prop].toLowerCase().indexOf(searchKey) !== -1) {
            searchOK = true;
          }
        });
      } else {
        searchOK = true;
      }

      if (schooltypesOK && locationsOK && schoolyearsOK && productsOK && searchOK) {
        teachersFiltered.push(teacher);
      }
    });

    // sort
    teachersFiltered = this.sort(teachersFiltered);

    this.setState((prevState) => {
      prevState.currData.teachers = teachersFiltered;
      prevState.currData.offset = 0;

      return prevState;
    });
  }

  handlePaginationChange(goto) {
    this.setState((prevState) => {
      prevState.currData.offset = goto;

      return prevState;
    });
  }

  downloadCSV() {
    const CSV_FILENAME = 'Insegnanti-' + this.state.filters.locations.join('.') + '-' + this.state.filters.schoolyears.join('.') + this.state.filters.products.join('.') + '-' + (new Date()).getTime() + '.csv';
    const items = this.state.currData.teachers;
    if (!items || !items.length) {
      return;
    }

    CSV.export(CSV_FILENAME, items, ';');
  }

  handleRecipientListUpdate(recipients) {
    // console.log('handleRecipientListUpdate() from TeachersOverviewPage');

    if (this.props.onRecipientListUpdate != undefined) {
      // console.log('Call App.onRecipientListUpdate()');

      this.props.onRecipientListUpdate(recipients, 'teachers');
    } else {
      // console.dir(this.props);
    }
  }

  handleSelectAllRecipients() {
    if (window.confirm('Sei sicuro/a di voler selezionare tutti i risultati in tutte le pagine..?') == false) {
      return;
    }

    let recipientsById = {};
    this.state.currData.teachers.forEach((teacher) => {
      recipientsById[teacher.teacher_key] = teacher;
    });

    this.handleRecipientListUpdate(recipientsById, 'teachers');
  }

  render() {
    const fetching = Object.keys(this.state.queue).length;

    let dataLabel = '';
    if (this.state.filters.region) {
      let filtersLocations = [
        this.state.filters.region ? this.state.filters.region.name : null,
        this.state.filters.province ? this.state.filters.province.name : null,
        this.state.filters.city ? this.state.filters.city.name : null
      ].filter((location) => {
        return location != null;
      });
      if (filtersLocations.length) {
        dataLabel += ' in ' + filtersLocations.join('/');
      }
    }
    if (this.state.searchKey) {
      dataLabel += ' con "' + this.state.searchKey + '"';
    }

    return (
      <div>
        <PageHeader>
          <MainHeading
            icon="fa fa-chalkboard-teacher"
            title={fetching == false ? `Insegnanti (${this.state.currData.teachers.length})` : <span>Insegnanti <small><Spinner /></small></span>}
            descr="Panoramica insegnanti"
          />
        </PageHeader>

        <div className={'container animated ' + (this.state.showFiltersRestoreNotice ? 'fadeInDown' : 'fadeOutUp')}>
          <div className="row">
            <div className="col-12">
            <div className="popover fade bs-popover-top show" style={{position: 'absolute', top: '-55px', left: '15px'}}>
              <div className="arrow" style={{left: '50px'}}></div>

              <div class="popover-body">
                <i className="fa fa-check text-success" style={{fontSize: '20px', float: 'left', marginRight: '12px', marginTop: '10px'}} /> Filtri ripristinati come da ultima sessione di lavoro
              </div>
              </div>
            </div>
          </div>
        </div>

        <FiltersToolbar
          filters={['schooltypes', 'locations', 'schoolyears', 'projects']}
          collapsable={true}

          onChange={this.handleFilters}

          data={this.state.filters}
          appData={this.props.appData}
        />

        <SortingToolbar
          options={[
            {
              label: 'Insegnante/ nome',
              name: 'teacher_name'
            },
            {
              label: 'Insegnante/ email',
              name: 'teacher_email'
            },
            {
              label: 'Scuola/ nome',
              name: 'school_name'
            },
            {
              label: 'Scuola/ città',
              name: 'school_city_name'
            },
            {
              label: 'Scuola/ provincia',
              name: 'school_province_name'
            },
            {
              label: 'Scuola/ regione',
              name: 'school_region_name'
            },
            {
              label: 'Ordini',
              name: 'orders_num_orders'
            },
            {
              label: 'Ordini/ kit',
              name: 'orders_num_kits'
            }
          ]}
          sortBy={this.state.currData.sortBy}
          sortDir={this.state.currData.sortDir}

          onChange={this.handleSortingChange}
        />

        {
          !this.state.currData.teachers.length && !this.state.filters.schoolyears.length && !this.state.filters.locations.length && !this.state.filters.productGroups.length && !this.state.filters.products.length && !this.state.searchKey ? (
            <NoResultsMessage />
          ) : ''
        }

        {
          this.state.error ? (
            <div className="container">
              <div className="row">
                <div className="col-12">
                  <div className="alert alert-danger">
                    <i className="fa fa-exclamation-triangle" /> ERRORE! {this.state.error}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <PageDataWrapper
              appData={this.props.appData}

              data={{
                label: fetching == false ? this.state.currData.teachers.length + ' ' + (this.state.currData.teachers.length == 1 ? 'insegnante' : 'insegnanti') : <Spinner />, // keep some character in there, so that layout doesn't break
                descr: dataLabel,

                onExport: this.downloadCSV
              }}
              pagination={{
                count: this.state.currData.teachers.length,
                offset: this.state.currData.offset,
                limit: this.state.currData.limit,
                onChange: this.handlePaginationChange
              }}
              filters={{
                tabs: []
              }}
              search={{
                placeholder: 'Cerca insegnanti',
                onChange: this.handleSearchUpdate,
                onSubmit: null
              }}
              status={{
                fetching: fetching,
                errors: null
              }}
            >
              {
                Object.keys(this.props.appData.mailings.mailingPanelList.teachersById).length > 0 ? (
                  <p className="alert alert-info mt-4">
                    <i style={{fontSize: '17px'}} className="fa fa-envelope mr-2" />

                    {Object.keys(this.props.appData.mailings.mailingPanelList.teachersById).length} risultati su {this.state.currData.teachers.length} selezionati per mailing

                    {
                      Object.keys(this.props.appData.mailings.mailingPanelList.teachersById).length != this.state.currData.teachers.length ? (
                        <button className="btn btn-primary ml-2" onClick={this.handleSelectAllRecipients}>Seleziona tutti</button>
                      ) : ''
                    }
                  </p>
                ) : ''
              }

              {
                <TeacherList
                  teachers={this.state.currData.teachers.slice(this.state.currData.offset).slice(0, this.state.currData.limit)}

                  recipientList={this.props.appData.mailings.mailingPanelList.teachersById}
                  onRecipientListUpdate={this.handleRecipientListUpdate}
                />
              }
            </PageDataWrapper>
          )
        }
      </div>
    );
  }
}

export default TeachersOverviewPage;


// WEBPACK FOOTER //
// ./src/pages/teachers/TeacherOverviewPage.js